import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/services/auth-service";
import { Check, AlertTriangle, Loader2 } from "lucide-react";
import WhimsicalBackground from "@/components/WhimsicalBackground";

const VerifyEmailPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying');
    const [error, setError] = useState('');

    useEffect(() => {
        const verifyEmail = async () => {
            try {
                await api.post('/auth/account-confirm-email/', { "key": token });
                setStatus('success');
            } catch (error) {
                setStatus('error');
                // @ts-expect-error ignoring
                if (error.response?.status === 400) {
                    setError('This verification link is invalid or has expired.');
                } else {
                    setError('An error occurred while verifying your email. Please try again.');
                }
            }
        };

        if (token) {
            verifyEmail();
        } else {
            setStatus('error');
            setError('Invalid verification link.');
        }
    }, [token]);

    const getContent = () => {
        switch (status) {
            case 'verifying':
                return {
                    title: 'Verifying Your Email',
                    icon: <Loader2 className="w-12 h-12 text-primary animate-spin" />,
                    message: 'Please wait while we verify your email address...',
                    showButton: false
                };
            case 'success':
                return {
                    title: 'Email Verified!',
                    icon: <Check className="w-12 h-12 text-green-500" />,
                    message: 'Your email has been successfully verified. You can now access all features of WhimsyQuill.',
                    showButton: true
                };
            case 'error':
                return {
                    title: 'Verification Failed',
                    icon: <AlertTriangle className="w-12 h-12 text-destructive" />,
                    message: error,
                    showButton: true
                };
        }
    };

    const content = getContent();

    return (
        <div className="grid w-full p-4 place-items-center">
            <WhimsicalBackground />
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-4 text-center">
                    <div className="flex justify-center">
                        {content.icon}
                    </div>
                    <CardTitle className="text-2xl font-bold">
                        {content.title}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-center">
                    <p className="text-muted-foreground">
                        {content.message}
                    </p>
                    {content.showButton && (
                        <Button
                            className="w-full"
                            onClick={() => navigate('/home')}
                        >
                            Go to Home
                        </Button>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default VerifyEmailPage;
